import React, { useState } from 'react';
import { Plus, Edit, Trash2, Search, Users as UsersIcon } from 'lucide-react';
import UserForm from '../components/UserForm';
import UserFormInterno from '../components/UserFormInterno';
import UserFormExterno from '../components/UserFormExterno';
import { UserCard } from '../components/UserCard';
import ConfirmDialog from '../../../shared/ui/ConfirmDialog';
import ProtectedView from '../../auth/components/ProtectedView';
import { 
  isAdmin, 
  isExternal, 
  isClient,
  canViewUsers,
  canManageInternalUsers,
  canManageExternalUsers,
  getRoleDisplayName
} from '../../../utils/permissions';
import toast from 'react-hot-toast';
import { INTERNAL_COMPANY } from '../../../config/constants';

type User = {
  id: number;
  name: string;
  email: string;
  role: string;
  company?: string;
  createdBy: string;
  date: string;
};

type Props = {
  mockUsers: User[];
  currentUser: any;
  addUser: (u: Partial<User>) => void;
  updateUser: (id: string | number, u: Partial<User>) => void;
  deleteUser: (id: string | number) => void;
  selectedCompanyId?: string | number;
  companyName?: string;
  initialTab?: 'internal'|'external';
  companies?: any[];
};
const UsersView: React.FC<Props> = ({ mockUsers, currentUser, addUser, updateUser, deleteUser, selectedCompanyId, companyName, initialTab, companies }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
  const [tab, setTab] = useState<'internal'|'external'>(initialTab || 'internal');
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; userId: number | null }>({ isOpen: false, userId: null });
  const [searchQuery, setSearchQuery] = useState('');
  // normalize selected company: for external tab, ignore Finnapp (internal-only) so filter acts on external companies only
  const effectiveSelectedCompanyId = React.useMemo(() => {
    if (!selectedCompanyId) return undefined;
    if (tab !== 'external') return selectedCompanyId;
    // when viewing external users, treat internal company selection as "no filter" because it has no external users
    const selected = (companies || []).find(c => c.id === selectedCompanyId);
    if (selected && String(selected.name).toLowerCase().trim() === INTERNAL_COMPANY.NAME.toLowerCase()) return undefined;
    return selectedCompanyId;
  }, [selectedCompanyId, tab, companies]);

  // Check permissions based on tab
  const hasPermission = tab === 'internal' 
    ? canManageInternalUsers(currentUser) 
    : canManageExternalUsers(currentUser);

  const permissionMessage = tab === 'internal'
    ? 'Solo el Controller General puede gestionar usuarios internos de Finnapp.'
    : 'No tienes permisos para gestionar usuarios externos.';

  // Filtrar usuarios
  const filteredUsers = mockUsers
    .filter(u => {
      const userType = (u as any).userType?.toUpperCase();
      const userIsInternal = userType === 'INTERNAL';
      const userIsExternal = userType === 'EXTERNAL';

      // Internal tab: show internal users, optionally filtered by selectedCompanyId
      if (tab === 'internal') {
        const companyId = (u as any).companyId || (u as any).empresaId;
        return userIsInternal && (selectedCompanyId ? companyId === selectedCompanyId : true);
      }

      // External tab
      const currentUserIsExternalClient = isExternal(currentUser) && isClient(currentUser);
      if (currentUserIsExternalClient) {
        return userIsExternal && (((u as any).externalCompanyName || '').trim().toLowerCase() === (currentUser?.externalCompanyName || '').trim().toLowerCase());
      }

      if (effectiveSelectedCompanyId) {
        const companyId = (u as any).companyId || (u as any).empresaId;
        return userIsExternal && (companyId === effectiveSelectedCompanyId);
      }

      return userIsExternal;
    })
    .filter(u => {
      // Búsqueda por nombre, email o empresa
      const searchLower = searchQuery.toLowerCase();
      const matchesName = u.name.toLowerCase().includes(searchLower);
      const matchesEmail = u.email.toLowerCase().includes(searchLower);
      const matchesCompany = ((u as any).empresa?.name || u.company || '').toLowerCase().includes(searchLower);
      return matchesName || matchesEmail || matchesCompany;
    });

  return (
    <ProtectedView hasPermission={canViewUsers(currentUser)} message="Solo usuarios internos de Finnapp pueden gestionar usuarios.">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Gestión de Usuarios {companyName ? `— ${companyName}` : ''}
            </h1>
            <p className="text-slate-400">Administra los usuarios del sistema</p>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission && (
              <button 
                onClick={() => { setEditingUser(undefined); setShowForm(true); }} 
                className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Nuevo Usuario
              </button>
            )}
          </div>
        </div>

        {/* Stats & Search Bar */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Contador */}
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500/10 rounded-lg">
                <UsersIcon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {filteredUsers.length}
                </p>
                <p className="text-sm text-slate-400">
                  {filteredUsers.length === 1 ? 'Usuario' : 'Usuarios'} {tab === 'internal' ? 'internos' : 'externos'}
                </p>
              </div>
            </div>

            {/* Búsqueda */}
            <div className="relative flex-1 lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o empresa..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
                  w-full pl-10 pr-4 py-2.5 
                  bg-slate-900 border border-slate-600 rounded-lg
                  text-white placeholder-slate-400
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-colors
                "
              />
            </div>
          </div>
        </div>

        {/* Users Grid */}
        {filteredUsers.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
            <UsersIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg mb-2">No se encontraron usuarios</p>
            {searchQuery && (
              <p className="text-slate-500 text-sm">
                Intenta con otros términos de búsqueda
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onEdit={(u) => { setEditingUser(u); setShowForm(true); }}
                onDelete={(u) => setConfirmDelete({ isOpen: true, userId: u.id })}
                canManage={hasPermission}
                getRoleDisplayName={getRoleDisplayName}
              />
            ))}
          </div>
        )}

        {/* Forms */}
        {showForm && (
          tab === 'internal' ? (
            // @ts-ignore
            <UserFormInterno
              user={editingUser as any}
              companiesProp={companies || []}
              selectedCompanyId={selectedCompanyId}
              selectedCompanyName={companyName}
              onClose={() => setShowForm(false)}
              onSave={(data: any) => {
                const payload = { ...data, userType: 'internal', companyId: data.companyId ?? selectedCompanyId };
                if (editingUser?.id) {
                  updateUser(editingUser.id, payload);
                } else {
                  addUser(payload);
                }
                setShowForm(false);
              }}
            />
          ) : (
            // @ts-ignore
            <UserFormExterno
              user={editingUser as any}
              currentUser={currentUser}
              onClose={() => setShowForm(false)}
              onSave={(data: any) => {
                if (editingUser?.id) {
                  updateUser(editingUser.id, data);
                } else {
                  addUser(data);
                }
                setShowForm(false);
              }}
              companies={companies}
            />
          )
        )}

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDelete.isOpen}
          title="¿Eliminar usuario?"
          message="Esta acción no se puede deshacer. El usuario será eliminado permanentemente del sistema."
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
          onConfirm={() => {
            if (confirmDelete.userId !== null) {
              deleteUser(confirmDelete.userId);
            }
          }}
          onCancel={() => setConfirmDelete({ isOpen: false, userId: null })}
        />
      </div>
    </ProtectedView>
  );
};

export default UsersView;
